import auth0, { Auth0UserProfile, CheckSessionOptions } from "auth0-js";
import React from "react";
import ReactDOM from "react-dom";
import { AUTH_CONFIG } from "../authentication/auth0-variables";
import { DateFormat } from "../general/datetime-formatter/DateFormat";
import { TimeFormat } from "../general/datetime-formatter/TimeFormat";
import { ProfileDetails } from "../material-ui/ProfileContent";
import HandleAuth from "./HandleAuth";

export interface AuthenticationData {
  access_token: string,
  email?: string,
  expires_at: string,
  id_token: string,
  name: string,
  nickname: string,
  picture: string,
  updated_at: Date,
  user_id: string,
}

export interface AuthInterface {
  handleAuthentication: () => void;
  refreshSession: () => void;
  login: () => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  authVariables: AuthenticationData | null;
  onSessionUpdate: () => void
}

export interface Auth0Roles {
  id: string;
  name: string;
  description: string;
}

export interface MGMT_API_Response {
  access_token: string;
  scope: string;
  expires_in: number;
  token_type: string;
}



export class Auth implements AuthInterface {
  public authVariables: AuthenticationData | null = null;
  public onSessionUpdate: () => void = () => null;
  tokenRenewalTimeout: any;
  userProfile: auth0.Auth0UserProfile | null = null;
  // not yet used:
  // cb: ((error: any, result?: any) => void) | undefined;
  // handleAuthRoute: any;
  tokenAuthOptions: auth0.AuthOptions = {
    domain: AUTH_CONFIG.domain,
    clientID: AUTH_CONFIG.clientId,
    redirectUri: AUTH_CONFIG.callbackUrl,
    responseType: 'token id_token',
    scope: 'openid profile email read:current_user update:current_user_metadata',
    audience: process.env.REACT_APP_AUTH0_MANAGEMENT_AUDIENCE
  };
  //accessToken = Object.assign({}, this.tokenAuthOptions).responseType = 'token';
  auth = new auth0.WebAuth(this.tokenAuthOptions);
  public userRoles: Auth0Roles[] = [];
  public mgmt_token: MGMT_API_Response | null = null;



  login() {
    this.auth.authorize();
  }

  handleAuthentication() {
    this.auth.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken) {
        this.setSession(authResult);
      } else if (err) {
        if (err.errorDescription === 'NEW-SIGN-UP-PLEASE-VERIFY' || err.errorDescription === 'Please verify your email before logging in.') {
          ReactDOM.render(<HandleAuth errorMessage={'New User Sign Up - A verification email has been sent to you. Please verify your email before logging in.'} errorCode={err.code!} />, document.getElementById("root"));
        } else {
          alert(`Error : ${err.error}`);
        }
      }
    });
  }

  changePassword(callback: (success: boolean) => void) {
    this.auth.changePassword({
      connection: 'DeltaXML-DB',
      email: this.authVariables?.email!
    }, function (err, response) {
      if (err) {
        callback(false);
        console.error(err);
      } else {
        callback(true);
      }
    });
  }

  public refreshSession = () => {
    const options: CheckSessionOptions = {};
    this.auth.checkSession(options,
      (err: auth0.Auth0Error | null, result: auth0.Auth0DecodedHash) => {
        if (err) {
          if (err.code?.includes("login_required")) {
            // Please comment the line below after the dialog box implementation is complete.
            //ReactDOM.render(<HandleAuth errorMessage={err.error_description!} errorCode={err.code}/>, document.getElementById("root"));
          }
          else if (err.error_description === 'NEW-SIGN-UP-PLEASE-VERIFY' || err.error_description === 'Please verify your email before logging in.') {
            ReactDOM.render(<HandleAuth errorMessage={'New User Sign Up - A verification email has been sent to you. Please verify your email before logging in.'} errorCode={err.code!} />, document.getElementById("root"));
          }
          else {
            ReactDOM.render(<HandleAuth errorMessage={'Could not get a new token ' + err.error_description!} errorCode={err.code!} />, document.getElementById("root"));
          }
        } else {
          this.setSession(result);
        }
      }
    );
    }
  setSession = (authResult: auth0.Auth0DecodedHash) => {
    // Set the time that the access token will expire at
    if (!authResult.accessToken) {
      return;
    }

    let expiresAt = '0';
    let isExpired = true;
    if (authResult.expiresIn) {
      isExpired = this.isAuthenticationExpired(authResult.expiresIn)
      expiresAt = JSON.stringify(
        (authResult.expiresIn * 1000) + new Date().getTime()
      );
    }

    if (isExpired) return;
      this.auth.client.userInfo(authResult.accessToken, (err, user) => {
      // Following code only gives limited userinfo
      // See Getting the User Profile  https://auth0.com/docs/libraries/auth0js#getting-the-user-profile 
      // var auth0Manage = new auth0.Management({
      //   domain: 'YOUR_DOMAIN',
      //   token: 'ACCESS_TOKEN'
      // });
      if (err) {
        alert(`Error: ${err.error}`);
        return;
      }


      // below we apparently should use user.user_id but this is not defined
      // in the response from client.userInfo()
      // we are using 'sub' property as described in this thread:
      // https://community.auth0.com/t/missing-user-id-from-user-profile/11457
      
      var auth0Manage = new auth0.Management({
        domain: AUTH_CONFIG.domain,
        token: authResult.accessToken,
        scope: 'openid profile',
      });

      auth0Manage.getUser(user.sub, (err, userProfile) => {
        this.userProfile = userProfile;
        const accessToken = authResult.accessToken ? authResult.accessToken : '';
        const idToken = authResult.idToken ? authResult.idToken : '';
  
        this.authVariables = {
          id_token: idToken,
          access_token: accessToken,
          email: user.email,
          expires_at: expiresAt,
          name: user.name,
          nickname: user.nickname,
          picture: user.picture,
          updated_at: new Date(Date.parse(user.updated_at)),
          user_id: authResult.idTokenPayload.sub,
        };
        this.onSessionUpdate();
        this.scheduleRenewal();
      })
  
  })
    }

  updateUserMetadata(profileDetails: ProfileDetails) {

    var auth0Manage = new auth0.Management({
      domain: AUTH_CONFIG.domain,
      token: this.authVariables === null ? "" : this.authVariables?.access_token,
      scope: 'openid profile',
    });

    var userMetadata = {
      "given_name": (profileDetails.givenName) ? profileDetails.givenName.trim() : null,
      "family_name": (profileDetails.familyName) ? profileDetails.familyName.trim() : null,
      "company": (profileDetails.compName) ? profileDetails.compName.trim() : null,
      "phone_number": (profileDetails.phone) ? profileDetails.phone.trim() : null,
      "email": (profileDetails.emailAddress) ? profileDetails.emailAddress : null,
      "date_format": (profileDetails.dateFormat) ? profileDetails.dateFormat : DateFormat.Unix,
      "time_format": (profileDetails.timeFormat) ? profileDetails.timeFormat : TimeFormat.AmPm
    }

    auth0Manage.patchUserMetadata(this.authVariables!.user_id, userMetadata, (err: auth0.Auth0Error | null, result: Auth0UserProfile) => {
      if (err) {
        alert("Unable to update user details. Please contact DeltaXML for more info ");
      } else {
        auth0Manage.getUser(this.authVariables!.user_id, (err, userProfile) => {
        });
      }
    });

    auth0Manage.getUser(this.authVariables!.user_id, (err, userProfile) => {
      this.userProfile = userProfile;
    });
  }

  logout() {
    // logout use form the auth0
    window.location.href = `https://${AUTH_CONFIG.domain}/v2/logout?returnTo=${AUTH_CONFIG.redirectUrl}/&client_id=${AUTH_CONFIG.clientId}`;
  }

  isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    let time = this.authVariables?.expires_at || '';
    let expiresAt: number = (time.length > 0) ? JSON.parse(time) : new Date().getTime()
    return new Date().getTime() < expiresAt;
  }

  isAuthenticationExpired(expiresIn: number) {
    const adjustedExpiresIn = (expiresIn * 1000) + new Date().getTime();
    return !(new Date().getTime() < adjustedExpiresIn);
  }

  renewToken() {
    this.auth.checkSession({},
      (err: auth0.Auth0Error | null, result: auth0.Auth0DecodedHash) => {
        if (err) {
          ReactDOM.render(<HandleAuth errorMessage={'Could not get a new token ' + err.error_description!} errorCode={err.code!} />, document.getElementById("root"));
        } else {
          this.setSession(result);
        }
      }
    );
  }

  scheduleRenewal() {
    const expiresAt: number = JSON.parse(this.authVariables?.expires_at || '');
    const delay = expiresAt - Date.now();
    if (delay > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewToken();
      }, delay);
    }
  }
  
}

export const AuthContext = React.createContext(new Auth());